/* ManageImagesComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./ManageImagesRenderer");
const ImagesFeed = require("./PagingImagesFeed");
const LinksComponent = require("./column_renderers/links/LinksComponent");
const StatusComponent = require("./column_renderers/status/OsLinkStatusComponent");
const EnableComponent = require("./column_renderers/enable/OsLinkEnableComponent");
const HashComponent = require("./column_renderers/hash/OsLinkHashComponent");
const defaultColumnWithTitleComponent = require("./column_renderers/defaultColumnWithTitle/DefaultColumnWithTitleComponent");
const ProgressComponent = require("./column_renderers/upload_progress/UploadProgressComponent");

class ManageImagesComponent extends EventEmitter {
    constructor(context) {
        super();
        this._parentView = context;
        this._platformEntity = _.get(this._parentView, "_odmOsPlatform");
        this._storageFactory = _.get(this._parentView, "_storageFactory");
        this._gameEditor = _.get(this._parentView, "_gameEditor");
        this._logger = _.get(this._parentView, "logger");
        this._activeOrgId = this._gameEditor._activeOrgId;
        this._userSession = this._gameEditor._userSession;
        this._renderer = new Renderer(this);
        this._key = "manage-images";
        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        // Add event listeners here if needed
    }

    _unregisterListeners() {
        // Remove event listeners here if needed
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get pagingFeed() {
        return this._imagesFeed;
    }

    shutdown() {
        this._unregisterListeners();

        const shutdowns = [
            this._renderer.shutdown(),
            _.isUndefined(this._imagesFeed) ? Promise.resolve() : this._imagesFeed.shutdown(),
            _.isUndefined(this._userGridData) ? Promise.resolve() : this._userGridData.shutdown(),
        ];

        return Promise.all(shutdowns).then(
            () => {
                // Clean up renderer
                this._renderer = undefined;

                // Clean up feed
                this._imagesFeed = undefined;

                // Clean up storage
                this._userGridData = undefined;
                this._storageFactory = undefined;

                // Clean up entity references
                this._platformEntity = undefined;

                // Clean up other properties
                this._key = undefined;
                this._gameEditor = undefined;
                this._logger = undefined;
                this._activeOrgId = undefined;
                this._userSession = undefined;
                this._parentView = undefined;
            },
            (err) => {
                if (this._logger) {
                    this._logger.warn({error: err}, "ManageImagesComponent.shutdown() error");
                }
            }
        );
    }

    get columnConfig() {
        return {
            enabled: {
                label: "Enabled",
                width: 70,
                priority: 1,
                columnRendererComponent: EnableComponent,
                entityColumn: true,
                context: {
                    platformId: this._platformEntity.getId(),
                    platformDetails: this._parentView,
                    platformEntityDef: this._parentView._OdmOsPlatformEntity,
                },
                sortable: false,
            },
            name: {
                sort: "asc",
                label: "Name",
                width: 120,
                priority: 2,
                columnRendererComponent: defaultColumnWithTitleComponent,
            },
            description: {
                label: "Description",
                width: 380,
                priority: 3,
                columnRendererComponent: defaultColumnWithTitleComponent,
            },
            url: {
                label: "URL",
                width: 440,
                priority: 4,
                columnRendererComponent: defaultColumnWithTitleComponent,
            },
            size: {
                label: "Size",
                width: 80,
                priority: 5,
            },
            progress: {
                label: "Progress",
                width: 80,
                priority: 8,
                columnRendererComponent: ProgressComponent,
                entityColumn: true,
            },
            hash: {
                label: "Hash",
                width: 320,
                priority: 6,
                columnRendererComponent: HashComponent,
                sortable: false,
            },
            linkStatus: {
                label: "Status",
                width: 140,
                priority: 7,
                columnRendererComponent: StatusComponent,
                context: {linkStatusValues: this._parentView._OdmOsLinkEntity.linkStatusLabels},
                entityColumn: true,
            },
            action: {
                label: "Actions",
                width: 75,
                priority: 9,
                columnRendererComponent: LinksComponent,
                context: {gameEditor: this._gameEditor},
                entityColumn: true,
                sortable: false,
                isLockedRight: true,
            },
        };
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    whenModelIsReady() {
        return Promise.resolve().then(() => {
            return this._startImageLinksFeed();
        });
    }

    async _startImageLinksFeed() {
        return this._storageFactory.whenStorageIsReady().then(() => {
            this._userGridData = this._storageFactory.getRemoteStorageInstanceForEntityGrid(
                "entity_grid_gl",
                this._key
            );

            const userSorting = this._userGridData.getSorting() || {};
            const patchedSorting = this._patchSorting(userSorting);

            this._imagesFeed = new ImagesFeed({
                activeOrgId: this._activeOrgId,
                feedKey: this._key + "_feed",
                userSession: this._userSession,
                entityDef: this._platformEntity,
                columnConfig: this.columnConfig,
                additionalDataFields: [
                    "downloadAttempts",
                    "downloadStartedOn",
                    "downloadCompletedOn",
                    "uploadStartedOn",
                    "uploadCompletedOn",
                    "errorMsg",
                ],
                sort: patchedSorting,
            });

            return this._imagesFeed.whenFeedReady();
        });
    }

    _patchSorting(sorting) {
        // add second sorting order by name
        const patchedSorting = Object.assign({}, sorting);
        if (
            patchedSorting &&
            Object.keys(patchedSorting).length === 1 &&
            !patchedSorting.name &&
            this.columnConfig.name
        ) {
            patchedSorting.name = 1;
        }
        return patchedSorting;
    }

    _onUserSorted(sortData) {
        let sort = {};

        const sortCriteria =
            this.columnConfig[sortData.sortColumn].valuePath ||
            this.columnConfig[sortData.sortColumn].path ||
            sortData.sortColumn;

        sort[sortCriteria] = sortData.sort === "asc" ? 1 : -1;
        sort = this._patchSorting(sort);

        this._imagesFeed.setSort(sort);
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = ManageImagesComponent;
